import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import AppShell from '../layouts/AppShell';
import UserProfileButton from '../components/UserProfileButton';
import FlowSheetFluidSection from '../components/flowSheet/FlowSheetFluidSection';
import { getPatientById, getPatientDisplayName } from '../constants/mockPatients';
import { useSession } from '../contexts/SessionContext';
import { useLanguage } from '../contexts/LanguageContext';
import { patientPath } from '../constants/routes';
import { buildMergedFluidData } from '../utils/flowSheetData';

export default function FluidBalanceDetailScreen() {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    admittedPatients,
    sessionNotes,
    getFlowSheetData,
    getPatientTasks,
    addFluidInput,
    addFluidOutput,
    markFluidBalanceTaskComplete,
  } = useSession();
  const { t, language } = useLanguage();

  const patient = getPatientById(id, admittedPatients);
  if (!patient) {
    return (
      <AppShell>
        <div className="p-8 text-center text-gray-500">{t('patientNotFound')}</div>
      </AppShell>
    );
  }

  const displayName = getPatientDisplayName(patient, language);
  const flowSheetData = getFlowSheetData(id);
  const mergedFluids = buildMergedFluidData(sessionNotes, id, displayName, flowSheetData);
  const fluidTask = getPatientTasks(id).find((task) => task.type === 'fluid_balance');

  return (
    <AppShell>
      <header className="sticky top-0 z-30 border-b border-gray-200 bg-surface px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex min-w-0 items-center gap-2">
            <button
              type="button"
              onClick={() => navigate(patientPath(id))}
              className="shrink-0 text-primary"
              aria-label={t('back')}
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="min-w-0">
              <h1 className="text-page-title">{t('fluidBalance')}</h1>
              <p className="truncate text-xs text-gray-500">
                {displayName}
                {patient.room ? ` · ${t('room')} ${patient.room}` : ''}
              </p>
            </div>
          </div>
          <UserProfileButton />
        </div>
      </header>

      <div className="px-4 py-4">
        <FlowSheetFluidSection
          patientId={id}
          flowSheetData={flowSheetData}
          mergedFluids={mergedFluids}
          balanceConcern={null}
          fluidTask={fluidTask}
          addFluidInput={addFluidInput}
          addFluidOutput={addFluidOutput}
          onMarkFluidComplete={() => markFluidBalanceTaskComplete(id)}
          showSectionHeader={false}
        />
      </div>
    </AppShell>
  );
}
